import React, { useState, useEffect } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQAccordionProps {
  items: FAQItem[];
}

export default function FAQAccordion({ items }: FAQAccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!items || items.length === 0) return;

    // Generate valid Google FAQPage JSON-LD schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": items.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };

    // Clean up any existing schema tag to prevent duplicate injections on route swaps
    const existingScript = document.getElementById('faq-jsonld-schema');
    if (existingScript) {
      existingScript.remove();
    }

    // Inject the script element cleanly into the document head
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-jsonld-schema';
    script.innerHTML = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const tag = document.getElementById('faq-jsonld-schema');
      if (tag) {
        tag.remove();
      }
    };
  }, [items]);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="w-full mt-12 pt-10 border-t border-gray-900 flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-2xl font-bold text-gray-100">Frequently Asked Questions</h3>
        <p className="text-gray-400 text-sm">
          Quick, simple answers to the most common questions about designing custom QR codes.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {items.map((item, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="bg-gray-900/30 border border-gray-900 rounded-xl overflow-hidden transition-all duration-300"
            >
              {/* Question Header */}
              <button
                type="button"
                onClick={() => toggleAccordion(idx)}
                className="w-full flex items-center justify-between p-5 text-left text-gray-200 hover:text-gray-100 font-bold text-base focus:outline-none select-none transition-all"
              >
                <span>{item.question}</span>
                <span className={`h-6 w-6 rounded-lg bg-gray-900 border border-gray-800/80 flex items-center justify-center text-xs text-indigo-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                  {isOpen ? '−' : '＋'}
                </span>
              </button>

              {/* Answer Panel */}
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  isOpen ? 'max-h-96 border-t border-gray-900/60 bg-gray-900/10' : 'max-h-0'
                }`}
              >
                <p className="p-5 text-gray-400 text-sm leading-relaxed text-justify">
                  {item.answer}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
